import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useState } from 'react';
import { formatAUD, formatDateAU } from '../../../shared/format';
import { QASkeleton } from '../components/QASkeleton';
import type { ReportRow } from '../../../shared/api';

const REPORTS = [
  { id: 'reviews-due', name: 'Reviews due', desc: 'Clients with reviewDue ≤ today' },
  { id: 'compliance', name: 'Compliance status', desc: 'Per-client compliance pass/outstanding matrix' },
  { id: 'advice-summary', name: 'Advice activity', desc: 'Advice document counts by status + kind' },
  { id: 'fee-summary', name: 'Fee summary', desc: 'FUM and estimated annual fee per adviser' },
  { id: 'gift-list', name: 'Christmas gift list', desc: 'Households flagged for a gift, with the postal address' },
  { id: 'event-invites', name: 'Event invitations', desc: 'Households flagged for client events' },
  { id: 'birthdays', name: 'Birthday cards', desc: 'Adults in households flagged for birthdays, soonest first' },
] as const;

export function Reports() {
  const [active, setActive] = useState<typeof REPORTS[number]['id']>('reviews-due');
  const { data, isLoading } = useQuery({
    queryKey: ['report', active],
    queryFn: () => api<{ data: ReportRow[] }>(`/api/reports/${active}`),
  });

  return (
    <div>
      <div className="mb-6">
        <p className="qa-eyebrow mb-2">Reports</p>
        <h1 className="qa-h1">
          Operational <span className="qa-italic-accent">reads.</span>
        </h1>
        <p className="qa-deck mt-2 text-ink-soft">Reviews due, compliance posture, advice activity, fees and mailing lists, all exportable.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {REPORTS.map(r => (
          <button key={r.id} onClick={() => setActive(r.id)}
            className={`text-left p-4 border ${active === r.id ? 'border-rust bg-paper' : 'border-hairline-paper bg-paper hover:border-rust'}`}>
            <p className="qa-eyebrow">{r.name}</p>
            <p className="text-sm text-ink-mute mt-1">{r.desc}</p>
          </button>
        ))}
      </div>

      <div className="flex justify-end gap-2 mb-3">
        <a className="btn-ghost text-sm" href={`/api/reports/${active}?format=csv`}>Export CSV</a>
        <a className="btn-ghost text-sm" href={`/api/reports/${active}?format=xlsx`}>Export Excel</a>
      </div>

      {isLoading ? (
        <QASkeleton variant="table" />
      ) : (data?.data ?? []).length === 0 ? (
        <p className="text-sm text-ink-mute" data-testid="report-empty">No rows in this report.</p>
      ) : (
        <div className="overflow-x-auto border border-hairline">
          <table className="qa-table">
            <thead>
              <tr>
                {(data?.data?.[0] ? Object.keys(data.data[0]) : []).map(k => <th key={k}>{k.replace(/_/g, ' ').replace(/([a-z])([A-Z0-9])/g, '$1 $2')}</th>)}
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((row, i: number) => (
                <tr key={i}>
                  {Object.entries(row).map(([k, v]) => (
                    <td key={k} className={typeof v === 'number' ? 'num' : ''}>
                      {fmt(k, v)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmt(key: string, v: unknown): string {
  if (v == null) return '—';
  if (key.toLowerCase().includes('date') || key === 'reviewDue' || key === 'lastFileNote' || key === 'nextBirthday') return formatDateAU(v as string);
  if (typeof v === 'number' && (key.includes('fum') || key.includes('fee') || key.includes('cents'))) return formatAUD(v);
  return String(v);
}
